import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { getProductSlug, SITE_URL } from "@/utils/seo";
import { API, BACKEND_URL } from "@/config";
import { ArrowRight, Battery, Zap } from "lucide-react";

const SITE = SITE_URL;

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "ECV Rentals", item: `${SITE}/ecvs` },
  ],
};

const imgSrc = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${BACKEND_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

export default function ECVs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/products`),
      axios.get(`${API}/bundles`).catch(() => ({ data: { bundles: [] } })),
    ]).then(([pRes, bRes]) => {
      const ecvProducts = (pRes.data.products || []).filter(
        (p) => p && p.category === "ecvs" && p.website_visible !== false && p.active !== false
      );
      const ecvBundles = (bRes.data.bundles || []).filter(
        (b) => b && b.category === "ecvs" && b.website_visible !== false && b.active !== false
      ).map((b) => ({ ...b, is_bundle: true }));

      // Deduplicate: hide standalone products that are components inside a bundle
      const bundleComponentIds = new Set(
        ecvBundles.flatMap((b) => (b.components || []).map((c) => c.product_id).filter(Boolean))
      );
      const deduped = ecvProducts.filter((p) => !bundleComponentIds.has(p.id));

      setItems([...deduped, ...ecvBundles]);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <>
      <PageSEO
        title="ECV Rentals Near Disney World & Orlando | Enchanted Gear Rentals"
        description="Rent an ECV (Electric Convenience Vehicle) near Walt Disney World and Orlando. Delivered free to Disney resorts, Universal Orlando and area hotels. Reserve online today."
        canonical={`${SITE}/ecvs`}
        structuredData={breadcrumbSchema}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "var(--color-bg, #040A0F)" }}>
        {/* Hero */}
        <section
          className="pt-28 pb-12 text-center"
          style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}
        >
          <div className="container max-w-3xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">ECV Rentals</span>
            </nav>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-4">
              ECV Rentals Near Disney World
            </h1>
            <p className="text-[#B9C8D5] text-lg max-w-2xl mx-auto">
              Electric Convenience Vehicles delivered to your Disney resort, Universal Orlando hotel
              or vacation home. Keep the whole family moving — all day, every day.
            </p>
            <p className="mt-3 text-sm text-[#B9C8D5]">
              Free delivery · Enchanted Gear Rentals is not affiliated with The Walt Disney Company.
            </p>
          </div>
        </section>

        {/* Product grid */}
        <section className="container pb-20 pt-10">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl bg-[#0A1825] border border-[#FFD166]/10 animate-pulse h-80"
                />
              ))}
            </div>
          ) : items.length === 0 ? (
            /* Coming-soon placeholder — shows until admin adds ECV products */
            <div className="max-w-lg mx-auto text-center py-24">
              <div className="w-16 h-16 rounded-full bg-[#FFD166]/10 flex items-center justify-center mx-auto mb-6">
                <Zap className="w-8 h-8 text-[#FFD166]" />
              </div>
              <h2 className="font-serif text-2xl text-[#F6F1E4] mb-3">ECVs Coming Soon</h2>
              <p className="text-[#B9C8D5] mb-6">
                We're adding our ECV fleet to the site. In the meantime, reach out and we'll get
                you set up right away.
              </p>
              <Link to="/contact">
                <button className="btn-primary" data-testid="ecv-contact-cta">
                  Contact Us <ArrowRight className="inline w-4 h-4 ml-1" />
                </button>
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="ecv-grid">
              {items.map((p) => {
                const slug = getProductSlug(p);
                const img = imgSrc(p.image);
                return (
                  <Link
                    key={p.id}
                    to={`/ecvs/${slug}`}
                    className="group rounded-2xl bg-[#0A1825] border border-[#FFD166]/10 hover:border-[#FFD166]/40 overflow-hidden transition-all hover:-translate-y-1"
                    data-testid={`ecv-card-${p.id}`}
                  >
                    <div className="aspect-[4/3] bg-[#060f18] overflow-hidden">
                      {img ? (
                        <img
                          src={img}
                          alt={`${p.name} ECV rental near Walt Disney World`}
                          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                          width="400"
                          height="300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Battery className="w-12 h-12 text-[#FFD166]/20" />
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] uppercase tracking-widest text-[#B9C8D5] border border-[#FFD166]/20 px-2 py-0.5 rounded-full">
                          ECV
                        </span>
                        {p.is_bundle && (
                          <span className="text-[10px] text-[#FFD166]/60 border border-[#FFD166]/15 px-1.5 py-0.5 rounded-full">
                            Bundle
                          </span>
                        )}
                      </div>
                      <h2 className="font-serif text-xl text-[#F6F1E4] group-hover:text-[#FFD166] transition-colors">
                        {p.name}
                      </h2>
                      <p className="text-sm text-[#B9C8D5] mt-1 line-clamp-2">
                        {p.tagline || p.description?.slice(0, 80)}
                      </p>
                      <div className="mt-4 flex items-center justify-between">
                        <span className="text-[#FFD166] font-semibold">From ${p.tier_1_3}/day</span>
                        <span className="text-xs text-[#B9C8D5] flex items-center gap-1">
                          View details <ArrowRight className="w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        {/* CTA */}
        <section className="container pb-20 text-center">
          <div className="max-w-xl mx-auto rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-8">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-3">Ready to book your ECV?</h2>
            <p className="text-[#B9C8D5] mb-6">
              Select your dates and delivery location to check availability and confirm your reservation.
            </p>
            <Link to="/#trip-setup">
              <button className="btn-primary" data-testid="ecv-book-cta">
                Check Availability <ArrowRight className="inline w-4 h-4 ml-1" />
              </button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
